import { useEffect, useRef } from 'react';
import smsgatewayService from '../services/smsgateway.service';

const TERMINAL_STATUSES = new Set(['DELIVERED', 'FAILED', 'UNDELIVERED', 'READ']);

function isPendingOutbound(msg) {
    if (!msg || msg.messageType !== 'OUTGOING' || msg.id == null) {
        return false;
    }
    const status = String(msg.status || '').toUpperCase();
    return !TERMINAL_STATUSES.has(status);
}

function mergeDeliveryStatuses(currentChat, serverMessages) {
    if (!Array.isArray(currentChat) || !Array.isArray(serverMessages)) {
        return currentChat;
    }

    const byId = new Map(
        serverMessages
            .filter((m) => m && m.id != null)
            .map((m) => [Number(m.id), m])
    );

    let changed = false;
    const next = currentChat.map((msg) => {
        if (msg.id == null) {
            return msg;
        }
        const server = byId.get(Number(msg.id));
        if (!server || !server.status) {
            return msg;
        }
        const nextStatus = String(server.status).toUpperCase();
        const currentStatus = String(msg.status || '').toUpperCase();
        if (nextStatus === currentStatus) {
            return msg;
        }
        changed = true;
        return { ...msg, status: nextStatus };
    });

    return changed ? next : currentChat;
}

/**
 * Polls the open conversation for outbound delivery status.
 * Covers localhost (no socket) and missed SMS_STATUS events.
 */
export function useOutboundDeliveryPolling({
    clinicId,
    selectedUser,
    chat,
    setChat,
    enabled = true,
}) {
    const chatRef = useRef(chat);
    chatRef.current = chat;

    const hasPending = Array.isArray(chat) && chat.some(isPendingOutbound);

    useEffect(() => {
        if (!enabled || !clinicId || !selectedUser || !hasPending) {
            return undefined;
        }

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 40; // ~2 minutes at 3s

        const poll = () => {
            attempts += 1;
            smsgatewayService
                .getConversation(clinicId, selectedUser, 0, 50)
                .then((res) => {
                    if (cancelled) {
                        return;
                    }
                    setChat((prev) => mergeDeliveryStatuses(prev, res.data));
                })
                .catch(() => {
                    // Silent — socket path / next tick may still succeed
                });
        };

        // First poll soon after send, then every 3s
        const initialTimer = window.setTimeout(poll, 1500);
        const intervalId = window.setInterval(() => {
            if (attempts >= maxAttempts) {
                window.clearInterval(intervalId);
                return;
            }
            // Stop early if nothing pending in latest chat
            if (!chatRef.current?.some(isPendingOutbound)) {
                window.clearInterval(intervalId);
                return;
            }
            poll();
        }, 3000);

        return () => {
            cancelled = true;
            window.clearTimeout(initialTimer);
            window.clearInterval(intervalId);
        };
    }, [enabled, clinicId, selectedUser, hasPending, setChat]);
}

export default useOutboundDeliveryPolling;
